import type { PlanCell, PlanOperation, StructuralObject } from '../../../types/plan';
import type { PlanningActionType } from '../../../types/planManagement';
import { adjacentPlanCoordinate, planCellKey, splitFourConnectedCellGroups } from '../../../utils/planCells';
import i18n from '../../../i18n';

/** Labels resolve on access, so they follow the current UI language. */
const tr = (key: string): string => i18n.t(`planning:structures.${key}`);

export type ShadeStructureAction = Extract<
  PlanningActionType,
  'outbuilding' | 'solid-fence' | 'low-fence' | 'lamp-post' | 'gate' | 'double-gate'
>;

interface ShadeStructurePreset {
  action: ShadeStructureAction;
  label: string;
  hint: string;
  kind: 'infrastructure' | 'device';
  subtype: string;
  /** Без имени подпись объекта берётся из каталога клиента по подтипу. */
  name?: string;
  singleCell: boolean;
  parameters: Record<string, unknown>;
  /** Калитка (1) или распашные ворота (2): объект-проём, врезается в забор. */
  gateLeaves?: 1 | 2;
}

export const SHADE_STRUCTURE_PRESETS: Record<ShadeStructureAction, ShadeStructurePreset> = {
  outbuilding: {
    action: 'outbuilding',
    get label() { return tr('outbuilding.label'); },
    get hint() { return tr('outbuilding.hint'); },
    kind: 'infrastructure',
    subtype: 'outbuilding',
    get name() { return tr('outbuilding.name'); },
    singleCell: false,
    parameters: {
      casts_shade: true,
      // Regular barn ridge height. Small sheds are ~2.5 m; pick that per-object if needed.
      shade_height_m: 3.5,
      shade_opacity: 1,
    },
  },
  'solid-fence': {
    action: 'solid-fence',
    get label() { return tr('solidFence.label'); },
    get hint() { return tr('solidFence.hint'); },
    kind: 'infrastructure',
    subtype: 'solid_fence',
    get name() { return tr('solidFence.name'); },
    singleCell: false,
    parameters: {
      casts_shade: true,
      shade_height_m: 2,
      shade_opacity: 1,
    },
  },
  // Изгородь — такой же объект-секция, как сплошной забор: одна связная
  // линия клеток, цвет секции — parameters.fence_color. Тени не даёт.
  // Имени нет: подпись («Изгородь» / "Low fence") каждый клиент берёт из
  // своего каталога на языке игрока.
  'low-fence': {
    action: 'low-fence',
    get label() { return tr('lowFence.label'); },
    get hint() { return tr('lowFence.hint'); },
    kind: 'infrastructure',
    subtype: 'low_fence',
    singleCell: false,
    parameters: {
      casts_shade: false,
      shade_height_m: 0.5,
    },
  },
  // Калитка и ворота — проём в заборе: прямая линия клеток, стиль/цвет/высота
  // берутся от забора-хозяина (см. buildGateOps), поэтому здесь только leaves.
  gate: {
    action: 'gate',
    get label() { return tr('gate.label'); },
    get hint() { return tr('gate.hint'); },
    kind: 'infrastructure',
    subtype: 'gate',
    singleCell: false,
    parameters: { leaves: 1 },
    gateLeaves: 1,
  },
  'double-gate': {
    action: 'double-gate',
    get label() { return tr('doubleGate.label'); },
    get hint() { return tr('doubleGate.hint'); },
    kind: 'infrastructure',
    subtype: 'gate',
    singleCell: false,
    parameters: { leaves: 2 },
    gateLeaves: 2,
  },
  'lamp-post': {
    action: 'lamp-post',
    get label() { return tr('lampPost.label'); },
    get hint() { return tr('lampPost.hint'); },
    kind: 'device',
    subtype: 'lamp_post',
    get name() { return tr('lampPost.name'); },
    singleCell: true,
    parameters: {
      casts_shade: true,
      shade_height_m: 2,
      shade_width_m: 0.15,
      shade_opacity: 1,
    },
  },
};

export const SHADE_STRUCTURE_ACTIONS = Object.keys(
  SHADE_STRUCTURE_PRESETS,
) as ShadeStructureAction[];

export const isShadeStructureAction = (
  action: PlanningActionType,
): action is ShadeStructureAction => action in SHADE_STRUCTURE_PRESETS;

export const buildShadeStructureOperation = (
  action: ShadeStructureAction,
  cells: Array<{ row: number; col: number }>,
): PlanOperation => {
  const preset = SHADE_STRUCTURE_PRESETS[action];
  return {
    op: 'createStructuralObject',
    cells,
    kind: preset.kind,
    subtype: preset.subtype,
    ...(preset.name ? { name: preset.name } : {}),
    parameters: { ...preset.parameters },
  };
};

const SOLID_FENCE_SUBTYPE = 'solid_fence';
const LOW_FENCE_SUBTYPE = 'low_fence';
const GATE_SUBTYPE = 'gate';

const normalizeSubtype = (value: unknown): string => String(value ?? '').trim().toLowerCase();

/** Забор или изгородь: клетки такого объекта — одна связная линия-секция. */
export const isFenceSubtype = (subtype: unknown): boolean => {
  const normalized = normalizeSubtype(subtype);
  return normalized === SOLID_FENCE_SUBTYPE || normalized === LOW_FENCE_SUBTYPE;
};

/** Калитка/ворота — отдельный подтип, в «забор или изгородь» не входит. */
export const isGateSubtype = (subtype: unknown): boolean => normalizeSubtype(subtype) === GATE_SUBTYPE;

export type GateAction = Extract<ShadeStructureAction, 'gate' | 'double-gate'>;

export const isGateAction = (action: PlanningActionType): action is GateAction =>
  action === 'gate' || action === 'double-gate';

export type GateCellsError = 'GATE_CELLS_NOT_STRAIGHT' | 'GATE_WIDTH';

export const GATE_CELLS_ERROR_MESSAGES: Record<GateCellsError, string> = {
  get GATE_CELLS_NOT_STRAIGHT() { return tr('gateErrors.notStraight'); },
  get GATE_WIDTH() { return tr('gateErrors.width'); },
};

/**
 * Геометрия проёма: клетки на одной прямой (одна строка или один столбец),
 * связной цепочкой без пропусков (ноль пропускается, как в splitFourConnectedCellGroups),
 * шириной 2 клетки для калитки и 4–6 для ворот.
 */
export const validateGateCells = (
  cells: Array<{ row: number; col: number }>,
  leaves: 1 | 2,
): GateCellsError | null => {
  if (cells.length === 0) return 'GATE_WIDTH';
  const sameRow = cells.every((cell) => cell.row === cells[0].row);
  const sameCol = cells.every((cell) => cell.col === cells[0].col);
  if (!sameRow && !sameCol) return 'GATE_CELLS_NOT_STRAIGHT';
  const groups = splitFourConnectedCellGroups(cells);
  if (groups.length !== 1) return 'GATE_CELLS_NOT_STRAIGHT';
  const width = groups[0].length;
  const widthOk = leaves === 1 ? width === 2 : width >= 4 && width <= 6;
  return widthOk ? null : 'GATE_WIDTH';
};

/** UUID v4 для id объекта, который клиент создаёт сам (бэкенд принимает только UUID). */
export const randomStructuralObjectId = (): string => {
  const cryptoApi = (globalThis as {
    crypto?: { randomUUID?: () => string; getRandomValues?: (array: Uint8Array) => Uint8Array };
  }).crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] % 16) + 0x40; // версия 4
  bytes[8] = (bytes[8] % 64) + 0x80; // вариант RFC 4122
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

interface StructuralObjectEraseInput {
  structuralObjectIds: string[];
  selectedCellKeys: Set<string>;
  structuralObjects: StructuralObject[];
  cells: PlanCell[];
  /** Источник id для кусков с окном действия; по умолчанию — случайный UUID. */
  generateId?: () => string;
}

/**
 * Операции стирания объектов, задетых выделением. Прочие объекты удаляются
 * целиком, а забор/изгородь подрезаются: объект удаляется и заново ставится
 * каждым оставшимся связным куском (тот же kind/subtype/name/parameters,
 * новые id). Сначала все удаления — их клетки освобождаются под куски.
 *
 * Окно действия (activeFrom/activeTo) при создании сервер ставит только
 * укрытиям, поэтому куску забора с окном клиент сам задаёт id и следом
 * возвращает окно через updateStructuralObject — иначе забор, скрытый до
 * даты установки, появился бы в мире на любой дате.
 */
export const buildStructuralObjectEraseOps = ({
  structuralObjectIds,
  selectedCellKeys,
  structuralObjects,
  cells,
  generateId = randomStructuralObjectId,
}: StructuralObjectEraseInput): PlanOperation[] => {
  const objectById = new Map(structuralObjects.map((item) => [item.id, item]));
  const deleteOps: PlanOperation[] = [];
  const recreateOps: PlanOperation[] = [];

  structuralObjectIds.forEach((structuralObjectId) => {
    deleteOps.push({ op: 'deleteStructuralObject', structuralObjectId });

    const structuralObject = objectById.get(structuralObjectId);
    if (!structuralObject || !isFenceSubtype(structuralObject.subtype)) return;

    const remainingCells = cells
      .filter((cell) => cell.structuralObjectId === structuralObjectId)
      .filter((cell) => !selectedCellKeys.has(planCellKey(cell.row, cell.col)))
      .map((cell) => ({ row: cell.row, col: cell.col }));

    const activeFrom = structuralObject.activeFrom ?? null;
    const activeTo = structuralObject.activeTo ?? null;
    const hasWindow = activeFrom !== null || activeTo !== null;

    splitFourConnectedCellGroups(remainingCells).forEach((piece) => {
      const pieceId = hasWindow ? generateId() : null;
      recreateOps.push({
        op: 'createStructuralObject',
        ...(pieceId ? { structuralObjectId: pieceId } : {}),
        cells: piece,
        kind: structuralObject.kind,
        subtype: structuralObject.subtype,
        ...(structuralObject.name ? { name: structuralObject.name } : {}),
        parameters: { ...(structuralObject.parameters ?? {}) },
      });
      if (pieceId) {
        recreateOps.push({ op: 'updateStructuralObject', structuralObjectId: pieceId, activeFrom, activeTo });
      }
    });
  });

  return [...deleteOps, ...recreateOps];
};

/**
 * Клетки выделения, которые стирает подрезка заборов и изгородей. Дорожка
 * под такой клеткой остаётся (как в Unity): ластик снимает забор, а не землю
 * под ним, поэтому эти клетки не перекрашиваются в empty.
 */
export const trimmedFenceCellKeys = ({
  structuralObjectIds,
  selectedCellKeys,
  structuralObjects,
  cells,
}: Omit<StructuralObjectEraseInput, 'generateId'>): Set<string> => {
  const fenceIds = new Set(structuralObjects
    .filter((item) => structuralObjectIds.includes(item.id) && isFenceSubtype(item.subtype))
    .map((item) => item.id));
  const keys = new Set<string>();
  cells.forEach((cell) => {
    if (!cell.structuralObjectId || !fenceIds.has(cell.structuralObjectId)) return;
    const key = planCellKey(cell.row, cell.col);
    if (selectedCellKeys.has(key)) keys.add(key);
  });
  return keys;
};

interface GateOpsInput {
  action: GateAction;
  /** Клетки проёма (уже проверены validateGateCells). */
  cells: Array<{ row: number; col: number }>;
  structuralObjects: StructuralObject[];
  planCells: PlanCell[];
  generateId?: () => string;
}

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

/** Параметры калитки от забора-хозяина (или дефолт: сплошной, высокий). */
const gateParametersFrom = (host: StructuralObject | null, leaves: 1 | 2): Record<string, unknown> => {
  const hostParams = host?.parameters ?? {};
  const low = host ? isFenceSubtype(host.subtype) && normalizeSubtype(host.subtype) === LOW_FENCE_SUBTYPE : false;
  const style = typeof hostParams.fence_style === 'string' && hostParams.fence_style.trim()
    ? hostParams.fence_style.trim()
    : 'solid';
  const color = typeof hostParams.fence_color === 'string' && HEX_COLOR_RE.test(hostParams.fence_color.trim())
    ? hostParams.fence_color.trim()
    : null;
  const shadeOpacity = typeof hostParams.shade_opacity === 'number' ? hostParams.shade_opacity : 1;
  return {
    leaves,
    fence_style: style,
    ...(color ? { fence_color: color } : {}),
    low,
    ...(low
      ? { casts_shade: false, shade_height_m: 0.8 }
      : { casts_shade: true, shade_height_m: 2, shade_opacity: shadeOpacity }),
  };
};

/** Две концевые клетки прямой линии и их внешние соседи (за концами линии). */
const gateLineEndNeighbours = (cells: Array<{ row: number; col: number }>): Array<{ row: number; col: number }> => {
  const sorted = [...cells].sort((a, b) => (a.row - b.row) || (a.col - b.col));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const horizontal = sorted.every((cell) => cell.row === first.row);
  if (horizontal) {
    return [
      { row: first.row, col: adjacentPlanCoordinate(first.col, -1) },
      { row: last.row, col: adjacentPlanCoordinate(last.col, 1) },
    ];
  }
  return [
    { row: adjacentPlanCoordinate(first.row, -1), col: first.col },
    { row: adjacentPlanCoordinate(last.row, 1), col: last.col },
  ];
};

/**
 * Пакет врезки калитки: заборы под клетками проёма удаляются и заново ставятся
 * оставшимися 4-связными кусками (тот же код, что у ластика — параметры, имя и
 * окно действия сохраняются), затем создаётся калитка. Стиль/цвет/low — от
 * забора под клетками; если его нет — от забора, примыкающего к концу линии;
 * иначе сплошной высокий забор без цвета (цвет по умолчанию).
 */
export const buildGateOps = ({
  action,
  cells,
  structuralObjects,
  planCells,
  generateId = randomStructuralObjectId,
}: GateOpsInput): PlanOperation[] => {
  const leaves = SHADE_STRUCTURE_PRESETS[action].gateLeaves ?? 1;
  const objectById = new Map(structuralObjects.map((item) => [item.id, item]));
  const cellByKey = new Map(planCells.map((cell) => [planCellKey(cell.row, cell.col), cell]));
  const selectedCellKeys = new Set(cells.map((cell) => planCellKey(cell.row, cell.col)));

  const fenceAt = (cell: { row: number; col: number }): StructuralObject | null => {
    const id = cellByKey.get(planCellKey(cell.row, cell.col))?.structuralObjectId;
    const item = id ? objectById.get(id) : undefined;
    return item && isFenceSubtype(item.subtype) ? item : null;
  };

  const hostIds: string[] = [];
  cells.forEach((cell) => {
    const host = fenceAt(cell);
    if (host && !hostIds.includes(host.id)) hostIds.push(host.id);
  });

  const styleSource = hostIds.length > 0
    ? objectById.get(hostIds[0]) ?? null
    : gateLineEndNeighbours(cells).map(fenceAt).find((item) => item !== null) ?? null;

  const trimOps = buildStructuralObjectEraseOps({
    structuralObjectIds: hostIds,
    selectedCellKeys,
    structuralObjects,
    cells: planCells,
    generateId,
  });

  const gateOp: PlanOperation = {
    op: 'createStructuralObject',
    cells: [...cells].sort((a, b) => (a.row - b.row) || (a.col - b.col)),
    kind: 'infrastructure',
    subtype: GATE_SUBTYPE,
    parameters: gateParametersFrom(styleSource, leaves),
  };

  return [...trimOps, gateOp];
};
